import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import {
  useMerchantApiMutation,
  useMerchantApiQuery,
  useMerchantApiQueryWithParams,
  useReactRouterLink,
} from "../../../hooks";
import {
  IModalFormWithTableProps,
  IPageAndSearchRequest,
  PagedMeta,
  IFormWithTableProps,
  IAccessClaim,
  IPermission,
  IViewTeamMember,
  IManageTeamMemberClaim,
} from "../../../interfaces-and-types";

import { View } from "./View";

export interface IManageTeamMemberFormViewProps
  extends IModalFormWithTableProps {
  isLoadingAvailableAccessClaims: boolean;
  isErrorAvailableAccessClaims: boolean;

  setClaimToAdd: (claim: string) => void;
  setClaimToRemove: (claim: string) => void;
  claimToAdd: string | null;
  claimToRemove: string | null;
  appendAccessClaimToUser: () => void;
  // removeAccessClaimFromUser: () => void;
  isSubmittingAppendClaimToUser: boolean;
  isSubmittingRemoveClaimFromUser: boolean;

  availableAccessClaims: IAccessClaim[];
  teamMember: IViewTeamMember | null;
}

export const ManageTeamMember = (props: IFormWithTableProps & {}) => {
  const { navigate } = useReactRouterLink();
  const [claimToAdd, setClaimToAdd] = useState<string | null>(null);
  const [claimToRemove, setClaimToRemove] = useState<string | null>(null);

  const { id } = useParams();

  const {
    isLoading: isLoadingGetTeamMemberById,
    isError: isErrorGetTeamMemberById,
    // isSuccess: isSuccessGetTeamMemberById,
    data: teamMember,
  } = useMerchantApiQueryWithParams<{ id: string }, IViewTeamMember>(
    [
      "getTeamMemberById",
      {
        id: id!,
      },
    ],
    {
      enabled: !!id,
    },
    (urlTemplate) => urlTemplate.replace("id", id!)
  );

  const closeModal = () => {
    navigate("/settings/team-and-security");
  };

  const {
    isLoading: isLoadingAvailableAccessClaims,
    isError: isErrorAvailableAccessClaims,
    // isSuccess: isSuccessAvailableAccessClaims,
    data: availableAccessClaims,
  } = useMerchantApiQueryWithParams<
    IPageAndSearchRequest,
    IAccessClaim[],
    PagedMeta
  >([
    "getAvailableAccessClaims",
    {
      page: 1,
      page_size: 500,
      search: null,
    },
  ]);

  // const {
  //   isLoading: isLoadingAvailablePermissions,
  //   isError: isErrorAvailablePermissions,
  //   // isSuccess: isSuccessAvailablePermissions,
  //   data: availablePermissions,
  // } = useMerchantApiQueryWithParams<
  //   IPageAndSearchRequest,
  //   IPermission[],
  //   PagedMeta
  // >([
  //   "getAvailablePermissions",
  //   {
  //     page: 1,
  //     page_size: 500,
  //     search: null,
  //   },
  // ]);

  const {
    isSuccess: isSuccessAddTeamMemberClaim,
    isLoading: isLoadingAppendClaimToUser,
    mutate: addTeamMemberAccessClaim,
  } = useMerchantApiMutation<IManageTeamMemberClaim, null>(
    "addTeamMemberAccessClaim",
    ["getTeamMemberById"],
    {},
    (urlTemplate) =>
      urlTemplate.replace("$team_member_id", id!).replace("$claim", claimToAdd!)
  ).usePostMutation;

  const {
    isSuccess: isSuccessRemoveTeamMemberClaim,
    isError: isErrorRemoveTeamMemberClaim,
    isLoading: isLoadingRemoveClaimFromUser,
    mutate: deleteTeamMemberAccessClaim,
  } = useMerchantApiMutation<IManageTeamMemberClaim, null>(
    "deleteTeamMemberAccessClaim",
    ["getTeamMemberById"],
    {},
    (urlTemplate) =>
      urlTemplate
        .replace("$team_member_id", id!)
        .replace("$claim", claimToRemove!)
  ).useDeleteMutation;

  useEffect(() => {
    if (!!claimToRemove) {
      if (
        window.confirm(
          `Are You Sure You Want To Remove This User From This Role? ${claimToRemove.toUpperCase()}`
        )
      )
        deleteTeamMemberAccessClaim();
      else setClaimToRemove(null);
    }
  }, [claimToRemove, deleteTeamMemberAccessClaim]);

  useEffect(() => {
    if (isSuccessAddTeamMemberClaim) {
      props.refreshTable();
      setClaimToAdd(null);
    }
  }, [isSuccessAddTeamMemberClaim]);

  useEffect(() => {
    if (isSuccessRemoveTeamMemberClaim) {
      props.refreshTable();
      setClaimToRemove(null);
    }
  }, [isSuccessRemoveTeamMemberClaim]);

  useEffect(() => {
    if (isErrorRemoveTeamMemberClaim) {
      setClaimToRemove(null);
    }
  }, [isErrorRemoveTeamMemberClaim]);

  const nonAddedAccessClaims =
    availableAccessClaims?.filter(
      (claim) =>
        teamMember?.user_access_claims &&
        [...teamMember.user_access_claims].find((cl) => cl === claim.claim) ===
          undefined
    ) || [];

  const appendAccessClaimToUser = () => {
    if (
      claimToAdd &&
      window.confirm("Are You Sure You Want To Add This User To This Role?")
    )
      addTeamMemberAccessClaim({
        team_member_id: id!,
        claim: claimToAdd,
      });
  };
  // const removeAccessClaimFromUser = () => deleteTeamMemberAccessClaim();z

  return (
    <View
      {...props}
      isModalOpen
      closeModal={closeModal}
      isLoadingModalData={isLoadingGetTeamMemberById}
      setClaimToAdd={setClaimToAdd}
      setClaimToRemove={setClaimToRemove}
      claimToAdd={claimToAdd}
      claimToRemove={claimToRemove}
      appendAccessClaimToUser={appendAccessClaimToUser}
      // removeAccessClaimFromUser={removeAccessClaimFromUser}
      isSubmittingAppendClaimToUser={isLoadingAppendClaimToUser}
      isSubmittingRemoveClaimFromUser={isLoadingRemoveClaimFromUser}
      isLoadingAvailableAccessClaims={isLoadingAvailableAccessClaims}
      isErrorAvailableAccessClaims={isErrorAvailableAccessClaims}
      availableAccessClaims={nonAddedAccessClaims}
      teamMember={teamMember || null}
    />
  );
};
